const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api'
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '')

class ApiError extends Error {
  constructor(message, status, details) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
  }
}

async function request(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
    ...options.headers,
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    body: options.body && typeof options.body !== 'string' ? JSON.stringify(options.body) : options.body,
  })

  const payload = response.status === 204 ? null : await response.json().catch(() => null)

  if (!response.ok) {
    throw new ApiError(payload?.message || 'Request failed', response.status, payload)
  }

  return payload
}

const apiClient = {
  login(credentials) {
    return request('/auth/login', {
      method: 'POST',
      body: credentials,
    })
  },

  register(payload) {
    return request('/auth/register', {
      method: 'POST',
      body: payload,
    })
  },

  requestPasswordReset(payload) {
    return request('/auth/password/forgot', {
      method: 'POST',
      body: payload,
    })
  },

  resetPassword(payload) {
    return request('/auth/password/reset', {
      method: 'POST',
      body: payload,
    })
  },

  me(token) {
    return request('/auth/me', { token })
  },

  userDashboard(token) {
    return request('/user/dashboard', { token })
  },

  weeklyBundle(token) {
    return request('/user/weekly-bundle', { token })
  },

  addPaymentMethod(token, payload) {
    return request('/user/payment-methods', {
      method: 'POST',
      token,
      body: payload,
    })
  },

  createPaymentSetupIntent(token) {
    return request('/user/payment-methods/setup-intent', {
      method: 'POST',
      token,
    })
  },

  completePaymentSetup(token, payload) {
    return request('/user/payment-methods/complete-setup', {
      method: 'POST',
      token,
      body: payload,
    })
  },

  setPrimaryPaymentMethod(token, methodId) {
    return request(`/user/payment-methods/${methodId}/primary`, {
      method: 'PATCH',
      token,
    })
  },

  removePaymentMethod(token, methodId) {
    return request(`/user/payment-methods/${methodId}`, {
      method: 'DELETE',
      token,
    })
  },

  updateProfile(token, payload) {
    return request('/user/profile', {
      method: 'PATCH',
      token,
      body: payload,
    })
  },

  updateNotificationPreferences(token, payload) {
    return request('/user/notification-preferences', {
      method: 'PATCH',
      token,
      body: payload,
    })
  },

  notifications(token, query = {}) {
    const params = new URLSearchParams()
    if (query.unread) params.set('unread', 'true')
    if (query.type) params.set('type', query.type)
    if (query.limit) params.set('limit', query.limit)
    const suffix = params.toString() ? `?${params.toString()}` : ''

    return request(`/user/notifications${suffix}`, { token })
  },

  payOrder(token, orderId) {
    return request(`/user/orders/${orderId}/pay`, {
      method: 'POST',
      token,
    })
  },

  checkoutWeeklyBundle(token) {
    return request('/user/weekly-bundle/checkout', {
      method: 'POST',
      token,
    })
  },

  addBuyNowToBundle(token, listingId) {
    return request(`/user/buy-now/${listingId}/bundle`, {
      method: 'POST',
      token,
    })
  },

  quickCheckoutBuyNow(token, listingId) {
    return request(`/user/buy-now/${listingId}/checkout`, {
      method: 'POST',
      token,
    })
  },

  searchBuyNow(query = {}) {
    const params = new URLSearchParams()
    if (query.q) params.set('q', query.q)
    if (query.category && query.category !== 'All') params.set('category', query.category)
    const suffix = params.toString() ? `?${params.toString()}` : ''

    return request(`/catalog/buy-now${suffix}`)
  },

  auctions(query = {}) {
    const params = new URLSearchParams()
    if (query.q) params.set('q', query.q)
    if (query.status && query.status !== 'all') params.set('status', query.status)
    if (query.category && query.category !== 'All') params.set('category', query.category)
    if (query.type && query.type !== 'all') params.set('type', query.type)
    const suffix = params.toString() ? `?${params.toString()}` : ''

    return request(`/catalog/auctions${suffix}`)
  },

  auctionDetail(auctionId) {
    return request(`/catalog/auctions/${auctionId}`)
  },

  auctionEventsUrl(auctionId) {
    return `${API_BASE_URL}/catalog/auctions/${auctionId}/events`
  },

  auctionFloorEventsUrl() {
    return `${API_BASE_URL}/catalog/auctions/events`
  },

  buyNowDetail(listingId) {
    return request(`/catalog/buy-now/${listingId}`)
  },

  featuredCatalog() {
    return request('/catalog/featured')
  },

  placeBid(token, auctionId, amount, requestId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`) {
    return request(`/user/auctions/${auctionId}/bids`, {
      method: 'POST',
      token,
      body: { amount, requestId },
    })
  },

  auctionBids(token, auctionId) {
    return request(`/user/auctions/${auctionId}/bids`, { token })
  },

  addToWatchlist(token, auctionId) {
    return request(`/user/watchlist/${auctionId}`, {
      method: 'POST',
      token,
    })
  },

  removeFromWatchlist(token, auctionId) {
    return request(`/user/watchlist/${auctionId}`, {
      method: 'DELETE',
      token,
    })
  },

  markNotificationRead(token, notificationId) {
    return request(`/user/notifications/${notificationId}/read`, {
      method: 'PATCH',
      token,
    })
  },

  markAllNotificationsRead(token) {
    return request('/user/notifications/read-all', {
      method: 'PATCH',
      token,
    })
  },

  adminOverview(token) {
    return request('/admin/overview', { token })
  },

  async downloadAdminExport(token, report) {
    const response = await fetch(`${API_BASE_URL}/admin/exports/${report}.csv`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    const payload = response.ok ? null : await response.json().catch(() => null)

    if (!response.ok) {
      throw new ApiError(payload?.message || 'Export failed', response.status, payload)
    }

    return response.blob()
  },

  adminMedia(token, query = {}) {
    const params = new URLSearchParams()
    if (query.q) params.set('q', query.q)
    if (query.limit) params.set('limit', query.limit)
    const suffix = params.toString() ? `?${params.toString()}` : ''

    return request(`/admin/media${suffix}`, { token })
  },

  async uploadImage(token, file, metadata = {}) {
    const body = new FormData()
    body.append('image', file)
    Object.entries(metadata).forEach(([key, value]) => {
      const normalized = String(value || '').trim()
      if (normalized) {
        body.append(key, normalized)
      }
    })

    const response = await fetch(`${API_BASE_URL}/admin/uploads`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body,
    })

    const payload = await response.json().catch(() => null)

    if (!response.ok) {
      throw new ApiError(payload?.message || 'Upload failed', response.status, payload)
    }

    return payload
  },

  updateMediaAsset(token, mediaId, payload) {
    return request(`/admin/media/${mediaId}`, {
      method: 'PATCH',
      token,
      body: payload,
    })
  },

  useMediaAsset(token, mediaId) {
    return request(`/admin/media/${mediaId}/use`, {
      method: 'POST',
      token,
    })
  },

  async uploadUserImage(token, file) {
    const body = new FormData()
    body.append('image', file)

    const response = await fetch(`${API_BASE_URL}/user/uploads`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body,
    })

    const payload = await response.json().catch(() => null)

    if (!response.ok) {
      throw new ApiError(payload?.message || 'Upload failed', response.status, payload)
    }

    return payload
  },

  createConsignment(token, payload) {
    return request('/user/consignments', {
      method: 'POST',
      token,
      body: payload,
    })
  },

  createAdminConsignment(token, payload) {
    return request('/admin/consignments', {
      method: 'POST',
      token,
      body: payload,
    })
  },

  bulkUpdateConsignments(token, payload) {
    return request('/admin/consignments/bulk-update', {
      method: 'POST',
      token,
      body: payload,
    })
  },

  createAuction(token, payload) {
    return request('/admin/auctions', {
      method: 'POST',
      token,
      body: payload,
    })
  },

  updateAuction(token, auctionId, payload) {
    return request(`/admin/auctions/${auctionId}`, {
      method: 'PATCH',
      token,
      body: payload,
    })
  },

  deleteAuction(token, auctionId) {
    return request(`/admin/auctions/${auctionId}`, {
      method: 'DELETE',
      token,
    })
  },

  createBuyNowListing(token, payload) {
    return request('/admin/buy-now', {
      method: 'POST',
      token,
      body: payload,
    })
  },

  updateBuyNowListing(token, listingId, payload) {
    return request(`/admin/buy-now/${listingId}`, {
      method: 'PATCH',
      token,
      body: payload,
    })
  },

  updateUser(token, userId, payload) {
    return request(`/admin/users/${userId}`, {
      method: 'PATCH',
      token,
      body: payload,
    })
  },

  sendNotification(token, payload) {
    return request('/admin/notifications', {
      method: 'POST',
      token,
      body: payload,
    })
  },

  relistOrder(token, orderId) {
    return request(`/admin/orders/${orderId}/relist`, {
      method: 'POST',
      token,
    })
  },

  sendOrderReminder(token, orderId) {
    return request(`/admin/orders/${orderId}/reminder`, {
      method: 'POST',
      token,
    })
  },

  sendEligibleOrderReminders(token) {
    return request('/admin/orders/reminders/send-eligible', {
      method: 'POST',
      token,
    })
  },

  shipOrder(token, orderId, trackingNumber) {
    return request(`/admin/orders/${orderId}/ship`, {
      method: 'POST',
      token,
      body: { trackingNumber },
    })
  },

  applyOrderPenalties(token) {
    return request('/admin/orders/apply-penalties', {
      method: 'POST',
      token,
    })
  },

  runOperationsJobs(token) {
    return request('/admin/jobs/run-operations', {
      method: 'POST',
      token,
    })
  },

  runAuctionLifecycle(token) {
    return request('/admin/auctions/run-lifecycle', {
      method: 'POST',
      token,
    })
  },

  updateConsignment(token, consignmentId, payload) {
    return request(`/admin/consignments/${consignmentId}`, {
      method: 'PATCH',
      token,
      body: payload,
    })
  },

  convertConsignment(token, consignmentId, payload) {
    return request(`/admin/consignments/${consignmentId}/convert`, {
      method: 'POST',
      token,
      body: payload,
    })
  },
}

function resolveAssetUrl(url) {
  if (!url) return url
  if (/^https?:\/\//i.test(url)) return url
  const assetUrl = `${API_ORIGIN}${url}`

  if (url.startsWith('/uploads/')) {
    const separator = assetUrl.includes('?') ? '&' : '?'
    return `${assetUrl}${separator}v=obc-assets-20260715`
  }

  return assetUrl
}

export { ApiError, API_BASE_URL, API_ORIGIN, resolveAssetUrl }
export default apiClient
